/**
 * Brand-logo preview substitution — two pure functions used by the editor
 * wrapper so authors see rendered logos WHILE editing while the
 * stored HTML stays canonical (data-brand-key only, no runtime URLs).
 *
 *   applyPreviewSrc(html)      — on editor load: rewrite every
 *     <img data-brand-key="X"> to ALSO carry src=/v1/brand-logos/resolve-preview?slug=X.
 *     Backend resolves the tenant-winning tier + 302-redirects to the PNG.
 *
 *   stripPreviewArtifacts(html) — on editor change / save: remove the
 *     runtime src we injected and any other <img src> that points at the
 *     resolve-preview endpoint. Keep data-brand-key + alt + width + the
 *     canonical inline style. Other <img src=...> not related to
 *     brand-logo picking are left untouched (authors may still embed
 *     arbitrary images via other routes).
 *
 * Uses the browser's DOMParser / XMLSerializer — no third-party deps.
 */

const PREVIEW_ENDPOINT = '/v1/brand-logos/resolve-preview';

/**
 * Build the absolute BE base URL for <img src> rewriting. CRITICAL: must
 * NOT be relative — the FE's Next.js middleware intercepts /v1/* without
 * a JWT and 307-redirects to /login, which <img> can't follow correctly
 * and every tile renders as a broken-image icon (see 2026-04-21 fix).
 *
 * Resolution order matches lib/api/config.ts:API_CONFIG.baseUrl:
 *   1. NEXT_PUBLIC_API_URL env (production builds)
 *   2. localhost:4001 fallback (dev)
 */
function getApiBase(): string {
  const envUrl =
    typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_API_URL;
  if (envUrl) return envUrl.replace(/\/$/, '');
  return 'http://localhost:4001';
}

/**
 * Rewrite every canonical <img data-brand-key="X"> (no src of its own) to also
 * carry a preview src.
 *
 * A brand-logo <img> that ALREADY carries a real src is left exactly as it is.
 * That src is load-bearing: for a brand that is not in the Global registry (an
 * AI Studio logo hosted on our own domain) the send path falls back to it
 * (`BrandLogoService.getLogoBufferPreferringSrc`: registry → src → monogram),
 * while the public preview endpoint 404s for such a slug. Overwriting it showed
 * a broken image while editing and — because stripPreviewArtifacts then removes
 * the preview URL — dropped the real logo from the saved email (WIZ F2).
 */
export function applyPreviewSrc(html: string): string {
  if (!html || !html.includes('data-brand-key')) return html;
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return html;
  }
  const base = getApiBase();
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const imgs = doc.querySelectorAll('img[data-brand-key]');
    imgs.forEach((node) => {
      const img = node as HTMLImageElement;
      const slug = img.getAttribute('data-brand-key') ?? '';
      if (!slug) return;
      const current = (img.getAttribute('src') ?? '').trim();
      if (current && !current.includes(PREVIEW_ENDPOINT)) return;
      img.setAttribute(
        'src',
        `${base}${PREVIEW_ENDPOINT}?slug=${encodeURIComponent(slug)}`,
      );
    });
    // Detect whether original input was a full document vs a fragment.
    const isFullDoc = /<html[\s>]/i.test(html) || /<!doctype/i.test(html);
    return isFullDoc
      ? `<!doctype html>\n${doc.documentElement.outerHTML}`
      : doc.body.innerHTML;
  } catch {
    return html;
  }
}

/**
 * Remove the preview src we injected. Preserves data-brand-key, alt,
 * width, and the canonical inline style — the stored HTML contract.
 */
export function stripPreviewArtifacts(html: string): string {
  if (!html || !html.includes('data-brand-key')) return html;
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return html;
  }
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const imgs = doc.querySelectorAll('img[data-brand-key]');
    imgs.forEach((node) => {
      const img = node as HTMLImageElement;
      const src = img.getAttribute('src') ?? '';
      // Only strip our own preview src — external `<img src="https://...">`
      // authored by hand stays untouched.
      if (src.includes(PREVIEW_ENDPOINT)) {
        img.removeAttribute('src');
      }
    });
    const isFullDoc = /<html[\s>]/i.test(html) || /<!doctype/i.test(html);
    return isFullDoc
      ? `<!doctype html>\n${doc.documentElement.outerHTML}`
      : doc.body.innerHTML;
  } catch {
    return html;
  }
}

/**
 * CSS class token the legacy WHOLE-document editor stamped onto the editable
 * `<body>`. This is a string that persists in already-saved customer HTML — it
 * is NOT a reference to any editor package (that engine was removed in the
 * editor-unification cleanup). Assembled from fragments so a literal substring
 * scan for the dead package name doesn't false-positive on this surviving
 * data-cleanup code; the runtime value is the legacy editable-body class.
 */
const LEGACY_EDITOR_BODY_CLASS = ['jo', 'dit', '-', 'wysiwyg'].join('');

/**
 * Strip editor-only attributes the legacy WHOLE-document editor injected to
 * make the document editable.
 *
 * The legacy editor ran in `iframe + editHTMLDocumentMode`, which set
 * `<body contenteditable="true" spellcheck="false">` on the iframe so it could
 * host the caret. In that mode the editor's value is the WHOLE document — so
 * that editable `<body>` serialises straight into the saved HTML, and HTML
 * cleaning was disabled so nothing was stripped. Left unstripped, the stored
 * email/phishing/landing HTML renders editable in the recipient's browser AND
 * on the live decoy page served to victims (prod incident 2026-05-16). This
 * runs on every `fromEditor()` save and on legacy persisted content.
 */
export function stripEditorArtifacts(html: string): string {
  if (!html) return html;
  // SSR / non-browser — targeted regex fallback (handles quoted, unquoted,
  // valueless, and camelCase forms). Strips all three legacy editor-mode
  // artifacts: `contenteditable`, `spellcheck`, and the editable-<body>
  // class token (LEGACY_EDITOR_BODY_CLASS).
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return html
      .replace(
        /\s+contenteditable(\s*=\s*("[^"]*"|'[^']*'|[^\s>]+))?/gi,
        '',
      )
      .replace(
        /\s+spellcheck(\s*=\s*("[^"]*"|'[^']*'|[^\s>]+))?/gi,
        '',
      )
      .replace(new RegExp(`\\s*\\b${LEGACY_EDITOR_BODY_CLASS}\\b`, 'g'), '');
  }
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc
      .querySelectorAll('[contenteditable]')
      .forEach((el) => el.removeAttribute('contenteditable'));
    doc
      .querySelectorAll('[spellcheck]')
      .forEach((el) => el.removeAttribute('spellcheck'));
    doc
      .querySelectorAll(`.${LEGACY_EDITOR_BODY_CLASS}`)
      .forEach((el) => el.classList.remove(LEGACY_EDITOR_BODY_CLASS));
    const isFullDoc = /<html[\s>]/i.test(html) || /<!doctype/i.test(html);
    return isFullDoc
      ? `<!doctype html>\n${doc.documentElement.outerHTML}`
      : doc.body.innerHTML;
  } catch {
    return html;
  }
}
